import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Package, Check, Phone, MapPin, ChevronRight, Loader2 } from "lucide-react";
import { API } from "@/lib/api";
import { useSEO } from "@/hooks/useSEO";
import ProductCard from "@/components/ProductCard";
import SHOP from "@/config/shop";

export default function HomePage() {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  useSEO(SHOP.tagline, `${SHOP.domain} - ${SHOP.description}`);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/products?limit=12&in_stock=true`),
      axios.get(`${API}/categories`)
    ]).then(([prodRes, catRes]) => {
      setFeaturedProducts(prodRes.data.products);
      const topCats = catRes.data.filter(c => c.level_depth === 2 && c.name && c.name !== 'Home').slice(0, 8);
      setCategories(topCats);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]"><Loader2 className="w-8 h-8 animate-spin text-[#004E98]" /></div>;
  }

  return (
    <div data-testid="home-page">
      <section className="bg-gradient-to-br from-[#004E98] to-[#003B73] text-white py-10 sm:py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-4">{SHOP.tagline}</h1>
              <p className="text-base sm:text-lg text-blue-100 mb-6">{SHOP.description}</p>
              <div className="flex flex-wrap gap-3">
                <Link to="/produkty" className="bg-[#FF6B35] text-white hover:bg-[#E85D2A] rounded-sm px-5 sm:px-6 py-3 font-medium uppercase tracking-wide text-sm transition-all shadow-sm" data-testid="hero-all-products-btn">
                  Vsechny produkty
                </Link>
                <Link to="/kontakty" className="bg-white/10 text-white hover:bg-white/20 border border-white/30 rounded-sm px-5 sm:px-6 py-3 font-medium uppercase tracking-wide text-sm transition-all">
                  Kontaktujte nas
                </Link>
              </div>
            </div>
            <div className="hidden md:block">
              <img src="https://images.unsplash.com/photo-1537655949728-d4e1c7c7bf90?w=600&h=400&fit=crop" alt={SHOP.name} className="rounded-md shadow-2xl" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-slate-800 mb-6">Kategorie produktu</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {categories.map(cat => (
              <Link key={cat.id_category} to={`/kategorie/${cat.id_category}`} className="bg-white border border-slate-200 rounded-md p-4 hover:border-[#004E98]/30 hover:shadow-md transition-all group" data-testid={`category-card-${cat.id_category}`}>
                <div className="w-12 h-12 bg-[#004E98]/10 rounded-md flex items-center justify-center mb-3 group-hover:bg-[#004E98]/20 transition-colors">
                  <Package className="w-6 h-6 text-[#004E98]" />
                </div>
                <h3 className="font-medium text-slate-800 group-hover:text-[#004E98] transition-colors">{cat.name.replace(/^\d+\./, '').trim()}</h3>
                {cat.children?.length > 0 && (
                  <p className="text-xs text-slate-400 mt-1">{cat.children.length} podkategorii</p>
                )}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-slate-800">Doporucene produkty</h2>
            <Link to="/produkty" className="text-[#004E98] hover:text-[#003B73] font-medium text-sm flex items-center gap-1">
              Zobrazit vse <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
            {featuredProducts.map(product => (
              <ProductCard key={product.id_product} product={product} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { icon: Package, title: SHOP.features[0].title, desc: SHOP.features[0].desc },
              { icon: Check, title: SHOP.features[1].title, desc: SHOP.features[1].desc },
              { icon: Phone, title: 'Podpora', desc: 'Po-Pa 8:00-17:00' },
              { icon: MapPin, title: 'Doprava zdarma', desc: 'Nad 2000 Kc' },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-4 p-4">
                <div className="w-12 h-12 bg-[#004E98] rounded-md flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800">{item.title}</h3>
                  <p className="text-sm text-slate-500">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
